import { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { ArrowRight, Undo2 } from 'lucide';

// common
import Icon from 'common/components/Icon';
import { PHONE_INPUT_NUMBER_REGEX, POSITIVE_NUMBER_REGEX } from 'config/regex';
import SchemaForm from 'common/SchemaForm';
import {
  genders,
  JobTitle,
  jobTitles,
  OrganizationTypeId,
  organizationTypes,
  orgIds,
} from 'enum/combobox';
import { datePickerCustomStyle } from 'common/SchemaForm/element/datepicker-element/utils';
import useUserRoles from 'common/hook/useUserRoles';

// screen
import type { Unit, UserInfo } from 'modules/account/types';
import useGetListGovAgencies from 'modules/account/services/useGetListGovAgencies';
import useGetListClients from 'modules/account/services/useGetListClients';
import useGetHvsInfo from 'modules/account/services/useGetHvsInfo';
import { useAccountCreateContext } from 'modules/account/create/AccountCreateContext';

const StepTwo = () => {
  const {
    state,
    handleSetUserInfo,
    handleBackToStepOne,
    handleSetUnitAddress,
    handleGoToStepThree,
    handleResetRolesToAssign,
  } = useAccountCreateContext();
  const { userInfo } = state;

  const [units, setUnits] = useState<Unit[]>([]);
  const [titles, setTitles] = useState<JobTitle[]>([]);

  const { isRAD, isSAD, isCSBAD, isNutriBAD, clientsOfCSBAD } = useUserRoles();
  const { data: govAgencies } = useGetListGovAgencies();
  const { data: clients } = useGetListClients();
  const { data: hvsInfo } = useGetHvsInfo();

  const userOrgTypeId = useMemo(() => userInfo.organizationTypeId, [userInfo.organizationTypeId]);

  const filterOrgTypes = useMemo(() => {
    if (isRAD) {
      if (!isSAD) return organizationTypes.filter(({ id }) => id === orgIds.system);
      return organizationTypes.filter(({ id }) => id === orgIds.system || id === orgIds.govAgency);
    }
    if (isSAD) {
      if ((isCSBAD && isNutriBAD) || isCSBAD) return organizationTypes;
      return organizationTypes.filter(({ id }) => id !== orgIds.client);
    }
    if (isCSBAD) return organizationTypes.filter(({ id }) => id === orgIds.client);
    return [];
  }, [isRAD, isSAD, isCSBAD, isNutriBAD]);

  const filteredOrgByCSBAD = useMemo(() => {
    const tempUnits: Unit[] = (clients || []).map((client) => ({
      id: client.operationCode,
      operationCode: client.operationCode,
      name: client.name,
      district: client.district || '',
      city: client.city || '',
      ward: client.wardName,
    }));
    if (!isCSBAD) return tempUnits;
    return tempUnits.filter((u) => clientsOfCSBAD.includes(u.operationCode));
  }, [clients, clientsOfCSBAD, isCSBAD]);

  const setUnitsByOrgId = useCallback(
    (orgId: OrganizationTypeId) => {
      switch (orgId) {
        case 'KHDN':
          setUnits(filteredOrgByCSBAD);
          break;
        case 'CQQL':
          setUnits(() =>
            (govAgencies || []).map((item) => ({
              id: item.operationCode,
              operationCode: item.operationCode,
              name: item.name,
              district: item.district || '',
              city: item.city || '',
              ward: item.ward || '',
            }))
          );
          break;
        default:
          setUnits(() => (hvsInfo && hvsInfo.company ? [hvsInfo.company] : []));
      }
    },
    [filteredOrgByCSBAD, govAgencies, hvsInfo]
  );

  const setUnitsByOrgIdRef = useRef(setUnitsByOrgId);
  setUnitsByOrgIdRef.current = setUnitsByOrgId;

  const handleSetUnitsByOrgId = useCallback(
    (orgId: OrganizationTypeId, form: UserInfo) => {
      setTitles(() => jobTitles.filter((t) => t.organizationTypeId === orgId));
      handleSetUserInfo({
        ...form,
        organizationTypeId: orgId,
        jobTitleId: '',
        unitId: '',
      });
      setUnitsByOrgId(orgId);
      handleResetRolesToAssign();
    },
    [handleSetUserInfo, setUnitsByOrgId, handleResetRolesToAssign]
  );

  useEffect(() => {
    if (!userOrgTypeId) {
      setUnits([]);
      setTitles([]);
      return;
    }
    setUnitsByOrgIdRef.current(userOrgTypeId);
    setTitles(jobTitles.filter((t) => t.organizationTypeId === userOrgTypeId));
  }, [userOrgTypeId, clients, govAgencies, hvsInfo]);

  return (
    <SchemaForm<UserInfo>
      formHookProps={{ values: userInfo }}
      onSubmit={handleGoToStepThree}
      schema={{
        fields: ({
          formProps: {
            intl: { formatMessage },
          },
          valuesField,
        }) => ({
          name: {
            mode: 'text-field',
            propsWrapper: { mobile: 6 },
            label: formatMessage({ id: 'account.fullname' }),
            placeholder: formatMessage({ id: 'account.enter.fullname' }),
            rules: {
              required: true,
              minLength: {
                value: 3,
                message: formatMessage({ id: 'account.error.invalidFullname' }),
              },
            },
          },
          organizationTypeId: {
            mode: 'select',
            propsWrapper: { tablet: 6 },
            label: formatMessage({ id: 'account.orgType' }),
            placeholder: formatMessage({ id: 'account.select.orgType' }),
            rules: { required: true },
            options: filterOrgTypes,
            rawOptions: true,
            disableClearBtn: true,
            onChange: (id: OrganizationTypeId) => handleSetUnitsByOrgId(id, valuesField),
          },
          genderId: {
            mode: 'select',
            propsWrapper: { tablet: 6 },
            label: formatMessage({ id: 'account.gender' }),
            placeholder: formatMessage({ id: 'account.select.gender' }),
            options: genders,
            rawOptions: true,
            disableClearBtn: true,
          },
          unitId: {
            mode: 'auto-complete',
            propsWrapper: { tablet: 6 },
            label: formatMessage({ id: 'account.unitName' }),
            placeholder: formatMessage({ id: 'account.select.unit' }),
            rules: { required: true },
            options: units,
            rawOptions: true,
            disableClearBtn: true,
            onChange: (item: Unit) => {
              const address = units.find((u) => u.operationCode === item.operationCode);
              if (address) handleSetUnitAddress(address);
            },
          },
          dob: {
            mode: 'datePicker',
            propsWrapper: { tablet: 6 },
            label: formatMessage({ id: 'account.dob' }),
            placeholder: formatMessage({ id: 'enter' }),
            disableFuture: true,
            ...datePickerCustomStyle,
          },
          jobTitleId: {
            mode: 'select',
            propsWrapper: { tablet: 6 },
            label: formatMessage({ id: 'account.jobTitle' }),
            placeholder: formatMessage({ id: 'account.select.jobTitle' }),
            rules: { required: true },
            options: titles,
            rawOptions: true,
            disableClearBtn: true,
            onChange: () => handleResetRolesToAssign(),
          },
          cccd: {
            mode: 'text-field',
            propsWrapper: { tablet: 6 },
            label: formatMessage({ id: 'account.identityNumber' }),
            placeholder: formatMessage({ id: 'account.enter.CCCD' }),
            rules: {
              pattern: {
                value: POSITIVE_NUMBER_REGEX,
                message: formatMessage({ id: 'account.invalid.cccd' }),
              },
            },
            showRequiredAsterisk: false,
          },
          jobPosition: {
            mode: 'text-field',
            propsWrapper: { mobile: 6 },
            label: formatMessage({ id: 'account.jobPosition' }),
            placeholder: formatMessage({ id: 'account.jobPosition' }),
          },
          issuePlace: {
            mode: 'text-field',
            propsWrapper: { tablet: 6 },
            label: formatMessage({ id: 'account.issuePlace' }),
            placeholder: formatMessage({ id: 'account.enter.issuePlaceIfCCCD' }),
            disabled: !valuesField.cccd,
          },
          address: {
            mode: 'text-field',
            propsWrapper: { tablet: 6 },
            label: formatMessage({ id: 'account.address' }),
            placeholder: formatMessage({ id: 'account.enter.address' }),
          },
          issueDate: {
            mode: 'datePicker',
            propsWrapper: { tablet: 6 },
            label: formatMessage({ id: 'account.issueDate' }),
            disableFuture: true,
            slotProps: {
              textField: {
                placeholder: formatMessage({ id: 'account.enter.issueDateIfCCCD' }),
                ...datePickerCustomStyle.slotProps.textField,
              },
            },
            disabled: !valuesField.cccd,
          },
          phone: {
            mode: 'text-field',
            propsWrapper: { tablet: 6 },
            label: formatMessage({ id: 'account.phoneNumber' }),
            placeholder: formatMessage({ id: 'account.enter.phoneNumber' }),
            rules: {
              required: true,
              pattern: {
                value: PHONE_INPUT_NUMBER_REGEX,
                message: formatMessage({ id: 'account.invalid.phoneNumber' }),
              },
            },
          },
          blank1: { mode: 'node' },
          back: {
            mode: 'button',
            variant: 'outlined',
            startIcon: <Icon icon={Undo2} size={16} />,
            children: formatMessage({ id: 'back' }),
            onClick: () => handleBackToStepOne(valuesField),
            propsWrapper: { mobile: true, display: 'flex', justifyContent: 'flex-end' },
          },
          submit: {
            mode: 'button',
            type: 'submit',
            variant: 'contained',
            endIcon: <Icon icon={ArrowRight} size={16} />,
            children: formatMessage({ id: 'continue' }),
            propsWrapper: { mobile: undefined },
          },
        }),
      }}
    />
  );
};

export default memo(StepTwo);
